import {createReducer, on} from '@ngrx/store';
import {
  addCadModel,
  addCadModelFailure,
  addCadModelRevision,
  addCadModelRevisionFailure,
  addCadModelRevisionSuccess,
  addCadModelSuccess,
  downloadCadModelRevision,
  downloadCadModelRevisionFailure,
  downloadCadModelRevisionSuccess,
  loadCadModels,
  loadCadModelsFailure,
  loadCadModelsSuccess,
  modifyCadModel,
  modifyCadModelFailure,
  modifyCadModelRevisionFailure,
  modifyCadModelRevisionSuccess,
  modifyCadModelSuccess,
  removeCadModel,
  removeCadModelFailure,
  removeCadModelRevision,
  removeCadModelRevisionFailure,
  removeCadModelRevisionSuccess,
  removeCadModelSuccess,
  uploadCadModelRevision,
  uploadCadModelRevisionFailure,
  uploadCadModelRevisionSuccess,
  uploadNewlyAddedCadModelRevisionDrawingFailure,
  uploadNewlyAddedCadModelRevisionDrawingSuccess,
  uploadNewlyAddedCadModelRevisionFailure,
  uploadNewlyAddedCadModelRevisionSuccess,
  validateCadModelRevision,
  validateCadModelRevisionFailure,
  validateCadModelRevisionSuccess,
  verifyDeleteCadModelRevisionIsValid,
  verifyDeleteCadModelRevisionIsValidFailure,
  verifyDeleteCadModelRevisionIsValidSuccess,
  loadArchivedCadModels,
  loadArchivedCadModelsSuccess,
  loadArchivedCadModelsFailure,
  archiveCadModel,
  archiveCadModelSuccess,
  archiveCadModelFailure,
  unArchiveCadModel,
  unArchiveCadModelSuccess,
  unArchiveCadModelFailure, setSelectedCadModelFromRoute, setSelectedCadModelFromTree, clearSelectedCadModel,
  generateIndentedBomReportCadModel,
  generateIndentedBomReportCadModelSuccess,
  generateIndentedBomReportCadModelFailure,
  generateIndentedBomReportCadModelRevision,
  generateIndentedBomReportCadModelRevisionSuccess,
  generateIndentedBomReportCadModelRevisionFailure,
} from "./cad-model.action";
import {
  CadModel,
  CadModelRevision,
} from "../../../shared/models/cad-model.model";
import {StateStatus} from "../../../shared/models/state-status.models";
import {VerifyDeleteCadModelRevisionIsValidResponse} from "../../models/cad-model.model";

export interface CadModelRevisionStatus {
  cadModelRevisionId: string;
  uploadStatus: StateStatus;
  downloadStatus: StateStatus;
  validationStatus: StateStatus;
  verifyDeleteStatus: StateStatus;
  indentedBomStatus: StateStatus;
}

export interface CadModelState {
  cadModels: CadModel[];
  archivedCadModels: CadModel[];
  error: string | null;
  status: StateStatus;
  revisionStatus: StateStatus;
  revisionValidationMessageStatus: StateStatus;
  archivedCadModelsStatus: StateStatus;
  generateIndentedBomReportStatus: StateStatus;
  
  verifyDeleteRevisionIsValidResponse: VerifyDeleteCadModelRevisionIsValidResponse | null;
  deleteRevisionVerificationStatus: StateStatus,

  uploadStatus: StateStatus;
  revisionDownloadStatus: StateStatus;
  validationStatus: StateStatus;
  selectedCadModelId: string | null;
  addedRevision: CadModelRevision | null;
  cadModelRevisionStatuses: CadModelRevisionStatus[];
}

export const initialState: CadModelState = {
  cadModels: [],
  archivedCadModels: [],
  error: null,
  status: 'pending' as StateStatus,
  revisionStatus: 'pending' as StateStatus,
  addedRevision: null,
  selectedCadModelId: null,
  uploadStatus: 'pending' as StateStatus,
  revisionDownloadStatus: 'pending' as StateStatus,
  validationStatus: 'pending' as StateStatus,
  revisionValidationMessageStatus: 'pending' as StateStatus,
  deleteRevisionVerificationStatus: 'pending' as StateStatus,
  verifyDeleteRevisionIsValidResponse: null,
  cadModelRevisionStatuses: [],
  archivedCadModelsStatus: 'pending' as StateStatus,
  generateIndentedBomReportStatus: 'pending' as StateStatus
};

export const cadModelReducer = createReducer(
  initialState,
  on(setSelectedCadModelFromRoute, setSelectedCadModelFromTree, (state, { selectedCadModelId }): CadModelState => ({
    ...state,
    selectedCadModelId: selectedCadModelId,
  })),
  on(clearSelectedCadModel, (state): CadModelState => ({
    ...state,
    selectedCadModelId: null,
  })),
  on(modifyCadModel, (state, { cadModel }): CadModelState => ({
    ...state,
    status: 'saving' as StateStatus,
    cadModels: state.cadModels.map(originalCadModel => {
      return originalCadModel.id === cadModel.id ? {
          ...originalCadModel,
          name: cadModel.name,
          description: cadModel.description,
          comments: cadModel.comments
        }
        : originalCadModel;
    })
  })),
  on(removeCadModel, (state, { cadModelId }): CadModelState => ({
    ...state,
    status: 'removing' as StateStatus,
  })),
  on(loadCadModels, (state) => ({ ...state, status: 'loading' as StateStatus })),
  on(loadCadModelsSuccess, (state, { cadModels }): CadModelState => ({
    ...state,
    cadModels: cadModels,
    error: null,
    status: 'loadSuccess' as StateStatus,
  })),
  on(loadCadModelsFailure, (state, { error }): CadModelState => ({
    ...state,
    error: error,
    status: 'error' as StateStatus,
  })),
  on(addCadModel, (state) => ({
    ...state,
    status: 'adding' as StateStatus,
  })),
  on(addCadModelSuccess, (state, { cadModel }): CadModelState => ({
    ...state,
    status: 'addSuccess' as StateStatus,
    cadModels: [
      cadModel,
      ...state.cadModels
    ],
  })),
  on(uploadNewlyAddedCadModelRevisionDrawingSuccess, (state, cadModelRevisionId): CadModelState => ({
    ...state,

  })),
  on(uploadNewlyAddedCadModelRevisionDrawingFailure, (state): CadModelState => ({
    ...state,

  })),
  on(uploadNewlyAddedCadModelRevisionSuccess, (state, {cadModelRevision}): CadModelState => ({
    ...state,
    cadModelRevisionStatuses: upsertCadModelRevisionUploadStatus(state.cadModelRevisionStatuses, cadModelRevision.id, 'loadSuccess'),
    cadModels: state.cadModels.map((cadModel: CadModel) => {
      return cadModelRevision.cadModelId === cadModel.id ?
        {
          ...cadModel,
          revisions: cadModel.revisions.map((cmr: CadModelRevision) => {
            return cmr.id === cadModelRevision.id ? cadModelRevision : cmr
          })
        } : cadModel
    }),
  })),
  on(uploadNewlyAddedCadModelRevisionFailure, (state, {cadModelRevisionId}): CadModelState => ({
    ...state,
    cadModelRevisionStatuses: upsertCadModelRevisionUploadStatus(state.cadModelRevisionStatuses, cadModelRevisionId, 'loadError'),
  })),
  on(addCadModelRevision, (state, { drawingFiles, revisionFile, cadModelRevision }): CadModelState => ({
    ...state,
    revisionStatus: 'adding' as StateStatus,
  })),
  on(addCadModelRevisionSuccess, (state, { cadModelRevision, revisionFile }): CadModelState => ({
    ...state,
    addedRevision: cadModelRevision,
    revisionStatus: 'addSuccess' as StateStatus,
    cadModelRevisionStatuses: upsertCadModelRevisionUploadStatus(state.cadModelRevisionStatuses, cadModelRevision.id, revisionFile?.files ? 'loading' : 'loadSuccess'),
    cadModels: state.cadModels.map((cadModel: CadModel) => {
      return cadModelRevision.cadModelId === cadModel.id ?
        {
          ...cadModel,
          revisions: cadModel.revisions ?[
            cadModelRevision,
            ...cadModel.revisions
          ] : [cadModelRevision]
        } : cadModel
    }),
  })),
  on(addCadModelRevisionFailure, (state): CadModelState => ({
    ...state,
    revisionStatus: 'error' as StateStatus,
  })),

  on(removeCadModelRevision, (state, {cadModelRevisionId}): CadModelState => ({
    ...state,
    cadModels: state.cadModels.map((cadModel: CadModel) => {
      return {
        ...cadModel,
        revisions: cadModel.revisions.filter(x => x.id !== cadModelRevisionId)
      }
    }),
    revisionStatus: 'removing' as StateStatus,
  })),

  on(removeCadModelRevisionSuccess, (state, { cadModelRevisionId }): CadModelState => ({
    ...state,
    error: null,
    revisionStatus: 'removeSuccess' as StateStatus,
  })),
  on(removeCadModelRevisionFailure, (state, { error }): CadModelState => ({
    ...state,
    error: error,
    revisionStatus: 'error' as StateStatus,
  })),

  on(addCadModelFailure, (state, { error }): CadModelState => ({
    ...state,
    cadModels: state.cadModels,
    error: error,
    status: 'error' as StateStatus,
  })),
  on(modifyCadModelSuccess, (state, { cadModel }): CadModelState => ({
    ...state,
    error: null,
    status: 'saveSuccess' as StateStatus,
  })),
  on(modifyCadModelFailure, (state, { error }): CadModelState => ({
    ...state,
    cadModels: state.cadModels,
    error: error,
    status: 'error' as StateStatus,
  })),
  on(removeCadModelSuccess, (state, { cadModelId }): CadModelState => ({
    ...state,
    cadModels: state.cadModels.filter((globalConfig) => globalConfig.id !== cadModelId),
    selectedCadModelId: null,
    error: null,
    status: 'removeSuccess' as StateStatus,
  })),
  on(modifyCadModelRevisionSuccess, (state): CadModelState => ({
    ...state,
    error: null,
    revisionStatus: 'saveSuccess' as StateStatus,
  })),
  on(modifyCadModelRevisionFailure, (state, { error }): CadModelState => ({
    ...state,
    error: error,
    revisionStatus: 'error' as StateStatus,
  })),
  on(removeCadModelFailure, (state, { error }): CadModelState => ({
    ...state,
    error: error,
    status: 'error' as StateStatus,
  })),
  on(downloadCadModelRevision, (state, {cadModelRevisionId}): CadModelState => ({
    ...state,
    cadModelRevisionStatuses: upsertCadModelRevisionDownloadStatus(state.cadModelRevisionStatuses, cadModelRevisionId, 'loading'),
  })),
  on(downloadCadModelRevisionSuccess, (state, { file, cadModelRevisionId }): CadModelState => ({
    ...state,
    revisionDownloadStatus: 'loadSuccess' as StateStatus,
    cadModelRevisionStatuses: upsertCadModelRevisionDownloadStatus(state.cadModelRevisionStatuses, cadModelRevisionId, 'loadSuccess'),

  })),
  on(downloadCadModelRevisionFailure, (state, { error }): CadModelState => ({
    ...state,
    error: error,
    revisionDownloadStatus: 'loadError' as StateStatus,
  })),
  on(uploadCadModelRevision, (state, {cadModelRevisionId}): CadModelState => ({
    ...state,
    cadModelRevisionStatuses: upsertCadModelRevisionUploadStatus(state.cadModelRevisionStatuses, cadModelRevisionId, 'loading'),
  })),
  on(uploadCadModelRevisionSuccess, (state, { cadModelRevision }): CadModelState => ({
    ...state,
    cadModelRevisionStatuses: upsertCadModelRevisionUploadStatus(state.cadModelRevisionStatuses, cadModelRevision.id, 'loadSuccess'),
    cadModels: state.cadModels.map((cadModel: CadModel) => {
      return cadModelRevision.cadModelId === cadModel.id ?
        {
          ...cadModel,
          revisions: cadModel.revisions.map((cmr: CadModelRevision) => {
            return cmr.id === cadModelRevision.id ? cadModelRevision : cmr
          })
        } : cadModel
    }),
  })),
  on(uploadCadModelRevisionFailure, (state, { error, cadModelRevisionId }): CadModelState => ({
    ...state,
    error: error,
    cadModelRevisionStatuses: upsertCadModelRevisionUploadStatus(state.cadModelRevisionStatuses, cadModelRevisionId, 'loadError'),

  })),
  on(validateCadModelRevision, (state, {cadModelRevisionId}): CadModelState => ({
    ...state,
    cadModelRevisionStatuses: upsertCadModelRevisionValidationStatus(state.cadModelRevisionStatuses, cadModelRevisionId, 'loading'),
  })),

  on(validateCadModelRevisionSuccess, (state, { cadModelRevision }): CadModelState => ({
    ...state,
    error: null,
    cadModels: state.cadModels.map((cadModel: CadModel) => {
      return cadModelRevision.cadModelId === cadModel.id ?
        {
          ...cadModel,
          revisions: cadModel.revisions.map((cmr: CadModelRevision) => {
            return cmr.id === cadModelRevision.id ? cadModelRevision : cmr
          })
        } : cadModel
    }),
    cadModelRevisionStatuses: upsertCadModelRevisionValidationStatus(state.cadModelRevisionStatuses, cadModelRevision.id, 'loadSuccess'),
  })),
  on(validateCadModelRevisionFailure, (state, { cadModelRevision }): CadModelState => ({
    ...state,
    cadModels: state.cadModels.map((cadModel: CadModel) => {
      return cadModelRevision.cadModelId === cadModel.id ?
        {
          ...cadModel,
          revisions: cadModel.revisions.map((cmr: CadModelRevision) => {
            return cmr.id === cadModelRevision.id ? cadModelRevision : cmr
          })
        } : cadModel
    }),
    validationStatus: 'loadError' as StateStatus,
  })),
  on(verifyDeleteCadModelRevisionIsValid, (state, { cadModelRevisionId }): CadModelState => ({
    ...state,
    cadModelRevisionStatuses: upsertCadModelRevisionVerifyDeleteStatus(state.cadModelRevisionStatuses, cadModelRevisionId, 'loading'),
  })),
  on(verifyDeleteCadModelRevisionIsValidSuccess, (state, {verifyDeleteIsValidResponse}): CadModelState => ({
    ...state,
    verifyDeleteRevisionIsValidResponse: verifyDeleteIsValidResponse,
    cadModelRevisionStatuses: upsertCadModelRevisionVerifyDeleteStatus(state.cadModelRevisionStatuses, verifyDeleteIsValidResponse.cadModelRevisionId, 'loadSuccess'),
  })),
  on(verifyDeleteCadModelRevisionIsValidFailure, (state, { error }): CadModelState => ({
    ...state,
    error: error,
    deleteRevisionVerificationStatus: 'loadError' as StateStatus,
  })),

  on(verifyDeleteCadModelRevisionIsValid, (state, { cadModelRevisionId }): CadModelState => ({
    ...state,
    deleteRevisionVerificationStatus: 'loading' as StateStatus
  })),
  on(verifyDeleteCadModelRevisionIsValidSuccess, (state, {verifyDeleteIsValidResponse}): CadModelState => ({
    ...state,
    verifyDeleteRevisionIsValidResponse: verifyDeleteIsValidResponse,
    deleteRevisionVerificationStatus: 'loadSuccess' as StateStatus,
  })),
  on(verifyDeleteCadModelRevisionIsValidFailure, (state, { error }): CadModelState => ({
    ...state,
    error: error,
    deleteRevisionVerificationStatus: 'loadError' as StateStatus,
  })),
  on(loadArchivedCadModels, (state) => ({
    ...state,
    archivedCadModelsStatus: 'loading' as StateStatus,
  })),
  on(loadArchivedCadModelsSuccess, (state, { archivedCadModels }): CadModelState => ({
    ...state,
    archivedCadModels,
    archivedCadModelsStatus: 'success' as StateStatus,
    error: null
  })),
  on(loadArchivedCadModelsFailure, (state, { error }) => ({
    ...state,
    archivedCadModelsStatus: 'error' as StateStatus,
    error
  })),
  on(generateIndentedBomReportCadModel, (state, { cadModelId }) => ({
    ...state,
    generateIndentedBomReportStatus: 'loading' as StateStatus
  })),
  on(generateIndentedBomReportCadModelSuccess, (state) => ({
    ...state,
    generateIndentedBomReportStatus: 'success' as StateStatus
  })),
  on(generateIndentedBomReportCadModelFailure, (state) => ({
    ...state,
    generateIndentedBomReportStatus: 'error' as StateStatus
  })),
  on(generateIndentedBomReportCadModelRevision, (state, { cadModelRevisionId }) => ({
    ...state,
    cadModelRevisionStatuses: upsertCadModelRevisionIndentedBomStatus(state.cadModelRevisionStatuses, cadModelRevisionId, 'loading' as StateStatus)
  })),
  on(generateIndentedBomReportCadModelRevisionSuccess, (state, { cadModelRevisionId }) => ({
    ...state,
    cadModelRevisionStatuses: upsertCadModelRevisionIndentedBomStatus(state.cadModelRevisionStatuses, cadModelRevisionId, 'success' as StateStatus)
  })),
  on(generateIndentedBomReportCadModelRevisionFailure, (state, { cadModelRevisionId }) => ({
    ...state,
    cadModelRevisionStatuses: upsertCadModelRevisionIndentedBomStatus(state.cadModelRevisionStatuses, cadModelRevisionId, 'error' as StateStatus)
  })),
  on(archiveCadModel, (state, { cadModelId }) => ({
    ...state,
    status: 'archiving' as StateStatus,
  })),
  on(archiveCadModelSuccess, (state, { cadModelId }) => ({
    ...state,
    archivedCadModels: [...state.archivedCadModels, {...state.cadModels.find(cm => cm.id == cadModelId) as CadModel, archived: true}],
    cadModels: state.cadModels.filter((cadModel) => {
      return cadModel.id != cadModelId
    }),
    error: null,
    status: 'archiveSuccess' as StateStatus,
  })),
  on(archiveCadModelFailure, (state, { error }) => ({
    ...state,
    error: error,
    status: 'archiveError' as StateStatus,
  })),
  on(unArchiveCadModel, (state, { cadModelId }): CadModelState => ({
    ...state,
    status: 'unArchiving' as StateStatus,
  })),
  on(unArchiveCadModelSuccess, (state, { cadModelId }): CadModelState => ({
    ...state,
    cadModels: [...state.cadModels, {...state.archivedCadModels.find(cm => cm.id == cadModelId) as CadModel, archived: false}],
    archivedCadModels: state.archivedCadModels.filter((cadModel) => {
      return cadModel.id != cadModelId
    }),
    error: null,
    archivedCadModelsStatus: 'unArchiveSuccess' as StateStatus,
  })),
  on(unArchiveCadModelFailure, (state, { error }): CadModelState => ({
    ...state,
    error: error,
    archivedCadModelsStatus: 'unArchiveError' as StateStatus,
  })),
);

function upsertCadModelRevisionUploadStatus(cadModelStatuses: CadModelRevisionStatus[], cadModelRevisionId: string, newStatus: StateStatus) {
  const currentStatus = cadModelStatuses.find(revStatus => revStatus.cadModelRevisionId == cadModelRevisionId);
  if (currentStatus){
    return cadModelStatuses.map(revStatus => revStatus.cadModelRevisionId == cadModelRevisionId  ? {
      ...revStatus,
      uploadStatus: newStatus,
    } : revStatus);
  } else {
    return [...cadModelStatuses, {
      uploadStatus: newStatus,
      downloadStatus: 'pending' as StateStatus,
      validationStatus: 'pending' as StateStatus,
      verifyDeleteStatus: 'pending' as StateStatus,
      indentedBomStatus: 'pending' as StateStatus,
      cadModelRevisionId: cadModelRevisionId

    }]
  }
}

function upsertCadModelRevisionDownloadStatus(cadModelStatuses: CadModelRevisionStatus[], cadModelRevisionId: string, newStatus: StateStatus) {
  const currentStatus = cadModelStatuses.find(revStatus => revStatus.cadModelRevisionId == cadModelRevisionId);
  if (currentStatus){
    return cadModelStatuses.map(revStatus => revStatus.cadModelRevisionId == cadModelRevisionId  ? {
      ...revStatus,
      downloadStatus: newStatus,
    } : revStatus);
  } else {
    return [...cadModelStatuses, {
      uploadStatus: 'pending' as StateStatus,
      downloadStatus: newStatus,
      validationStatus: 'pending' as StateStatus,
      verifyDeleteStatus: 'pending' as StateStatus,
      indentedBomStatus: 'pending' as StateStatus,
      cadModelRevisionId: cadModelRevisionId

    }]
  }
}

function upsertCadModelRevisionValidationStatus(cadModelStatuses: CadModelRevisionStatus[], cadModelRevisionId: string, newStatus: StateStatus) {
  const currentStatus = cadModelStatuses.find(revStatus => revStatus.cadModelRevisionId == cadModelRevisionId);
  if (currentStatus){
    return cadModelStatuses.map(revStatus => revStatus.cadModelRevisionId == cadModelRevisionId  ? {
      ...revStatus,
      validationStatus: newStatus,
    } : revStatus);
  } else {
    return [...cadModelStatuses, {
      uploadStatus: 'pending' as StateStatus,
      downloadStatus: 'pending' as StateStatus,
      verifyDeleteStatus: 'pending' as StateStatus,
      validationStatus: newStatus,
      indentedBomStatus: 'pending' as StateStatus,
      cadModelRevisionId: cadModelRevisionId,
    }]
  }
}

function upsertCadModelRevisionVerifyDeleteStatus(cadModelStatuses: CadModelRevisionStatus[], cadModelRevisionId: string, newStatus: StateStatus) {
  const currentStatus = cadModelStatuses.find(revStatus => revStatus.cadModelRevisionId == cadModelRevisionId);
  if (currentStatus){
    return cadModelStatuses.map(revStatus => revStatus.cadModelRevisionId == cadModelRevisionId  ? {
      ...revStatus,
      verifyDeleteStatus: newStatus as StateStatus,
    } : revStatus);
  } else {
    return [...cadModelStatuses, {
      uploadStatus: 'pending' as StateStatus,
      downloadStatus: 'pending' as StateStatus,
      validationStatus: 'pending' as StateStatus,
      verifyDeleteStatus: newStatus as StateStatus,
      indentedBomStatus: 'pending' as StateStatus,
      cadModelRevisionId: cadModelRevisionId
    }]
  }
}

function upsertCadModelRevisionIndentedBomStatus(cadModelStatuses: CadModelRevisionStatus[], cadModelRevisionId: string, newStatus: StateStatus) {
  const currentStatus = cadModelStatuses.find(revStatus => revStatus.cadModelRevisionId == cadModelRevisionId);
  if (currentStatus){
    return cadModelStatuses.map(revStatus => revStatus.cadModelRevisionId == cadModelRevisionId  ? {
      ...revStatus,
      indentedBomStatus: newStatus as StateStatus,
    } : revStatus);
  } else {
    return [...cadModelStatuses, {
      uploadStatus: 'pending' as StateStatus,
      downloadStatus: 'pending' as StateStatus,
      validationStatus: 'pending' as StateStatus,
      verifyDeleteStatus: 'pending' as StateStatus,
      indentedBomStatus: newStatus as StateStatus,
      cadModelRevisionId: cadModelRevisionId
    }]
  }
}
